import { Menu } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Datepicker from 'react-datepicker';
import { AiOutlineEye } from 'react-icons/ai';
import { BiChevronDown } from 'react-icons/bi';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import DateFormat from 'src/components/DateFormat';
import DateFormat2 from 'src/components/DateFormat/DateFormat2';
import Button from 'src/components/button';
import Loader from 'src/components/loader';
import {
  payment_status,
  PaymentStatusButton,
} from 'src/components/payment-status';
import Search from 'src/components/search';
import Table from 'src/components/table';
import { CurrencyAmount } from 'src/containers/currency';
import { triggerOrdersRefetch, updateOrderDate, updateOrderFilters, updateOrderItemsCache, updateOrderPaymentType } from 'src/redux/action/orderItems.action';
import { RootState } from 'src/redux/reducers';
import Service from 'src/setup/network';
import { endOfToday, oneWeekAgoDate } from 'src/utils/dateOneMonthAgo';

const Orders = () => {
  const dispatch = useDispatch()
  const [query, setQuery] = useState('');
  const { orderStartDate, orderEndDate, ordersCache, filters, orderPaymentType, refetch } = useSelector(({ orderItemsData }: RootState) => orderItemsData);
  const [initPage, setInitPage] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [orderData, setOrderData] = useState<any[]>(ordersCache?.data || []);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const openDisc = Boolean(anchorEl);
  const navigate = useNavigate();

  const getEndOfDay = (date: Date) => {
    const endOfDate = new Date(date);
    endOfDate.setHours(23, 59, 59, 999);
    return endOfDate;
  };
  const getStartOfDay = (date: Date) => {
    const startOfDate = new Date(date);
    startOfDate.setHours(0, 0, 0, 0);
    return startOfDate;
  };
  const [dateQuery, setDateQuery] = useState<any>(
    `&from=${getStartOfDay(orderStartDate || oneWeekAgoDate()).toISOString()}&to=${getEndOfDay(orderEndDate || endOfToday()).toISOString()}`
  );

  const fetchOrderData = useCallback(async (querySend: string, filters: any) => {
    setIsLoading(true);
    try {
      const res = await Service.get(`/payments/receipt${querySend}`);
      const data = await res?.data?.paginatedReceipts;
      setOrderData(data);
      dispatch(updateOrderFilters({page: initPage ? 1 : filters.page, limit: filters.limit, totalPages: res?.data?.totalPages}))
      dispatch(updateOrderItemsCache({ query: querySend, data }))
      setIsLoading(false);
      setInitPage(false)
    } catch (err) {}
  }, []);

  useEffect(() => {
    const paginationQuery = `&page=${filters.page}&limit=${filters.limit}`
    const querySend = `?searchQuery=${query}&paymentStatus=${orderPaymentType.value}${dateQuery}${paginationQuery}`;
    const isRefetchTriggered = refetch;
    if (ordersCache?.query === querySend && !isRefetchTriggered) {
      setOrderData(ordersCache.data);
    } else {
      fetchOrderData(querySend, filters);
    }
    if(isRefetchTriggered) {
      dispatch(triggerOrdersRefetch(false));
    }
  }, [dateQuery, query, orderPaymentType, filters, refetch]);

  const tableColumn = [
    {
      Header: 'Date',
      accessor: 'a',
      Cell: (row: any) => (
        <div className="flex flex-col w-[100px]">
          <div>
            <DateFormat text={row?.cell?.row?.original?.createdAt} />
          </div>
          <div className="text-secondary-0">
            <DateFormat2 text={row?.cell?.row?.original?.createdAt} />
          </div>
        </div>
      ),
    },
    {
      Header: 'Order ID',
      accessor: 'referenceNumber',
      Cell: (row: any) => (
        <p>{`#${row?.cell?.row?.original?.referenceNumber}`}</p>
      ),
    },
    {
      Header: 'User',
      accessor: 'user',
      Cell: (row: any) => (
        <>
          {row?.cell?.row?.original?.customer && (
            <div>
              <p>{`${row?.cell?.row?.original?.customer?.name}`}</p>
              <p className="text-secondary-0 text-sm">{`${row?.cell?.row?.original?.customer?.email}`}</p>
            </div>
          )}
        </>
      ),
    },
    {
      Header: 'Contact',
      accessor: 'o',
      Cell: (row: any) => (
        <>
          {row?.cell?.row?.original?.customer && (
            <div>
              <p>{`${row?.cell?.row?.original?.customer?.phone}`}</p>
            </div>
          )}
        </>
      ),
    },
    {
      Header: 'Payment method',
      accessor: 'origins',
      Cell: (row: any) => (
        <p>
          {row?.cell?.row?.original.status === 'not_paid' && 'Full credit'}
          {row?.cell?.row?.original?.transactions
            ? `${row?.cell?.row?.original?.transactions
                ?.map((x: any) =>
                  x.paymentMethod
                    ? `${x.paymentMethod} ${x.count > 1 ? 'x' + x.count : ''}`
                    : ''
                )
                .join(', ')}`
            : ''}
        </p>
      ),
    },
    {
      Header: 'Type',
      accessor: 'orderTypes',
      Cell: (row: any) => (
        <p className="text-secondary-0">
          {row?.cell?.row?.original?.orderType === 'dine_in'
            ? 'Dine In'
            : row?.cell?.row?.original?.orderType === 'delivery'
            ? 'Delivery'
            : row?.cell?.row?.original?.orderType === 'takeaway'
            ? 'Take Away'
            : ''}
        </p>
      ),
    },
    {
      Header: 'Table',
      accessor: '',
      Cell: (row: any) => <p>{row?.cell?.row?.original?.table?.name}</p>,
    },
    {
      Header: 'Amount',
      accessor: 'subTotals',
      Cell: (row: any) => (
        <div>
          {row?.cell?.row?.original?.subTotal != null ? (
            <div><CurrencyAmount amount={row?.cell?.row?.original?.total}/></div>
          ) : (
            ''
          )}
        </div>
      ),
    },
    {
      Header: '',
      accessor: 'subTotalss',
      Cell: (row: any) => {
        return PaymentStatusButton(row?.cell?.row?.original?.status);
      },
    },
    {
      Header: 'Action',
      accessor: 'action',
      Cell: (row: any) => (
        <div className="flex items-center">
          <div
            className="w-7 h-7 rounded-xl bg-light-grey bg-opacity-50 flex items-center justify-center"
            onClick={() => navigate(`/orders/${row?.cell?.row?.original?.id}`)}
          >
            <AiOutlineEye color="#4062FF" />
          </div>
        </div>
      ),
    },
  ];
  const handleDateChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    dispatch(updateOrderDate({orderStartDate: start, orderEndDate: end}))
    if (!start || !end) return;
    const qu = `&from=${getStartOfDay(start).toISOString()}&to=${getEndOfDay(
      end
    ).toISOString()}`;
    setDateQuery(qu);
  };

  const handleClickDiscMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseDiscMenu = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <>
        <div className="pb-10">
          <div className="flex justify-between">
            {selectedRows.length > 0 ? (
              <Button
                type="submit"
                text={'Make payment'}
                variant="primary"
                onClick={() => {
                  navigate('/payment/admin/multiple', {
                    state: { orders: selectedRows },
                  });
                }}
              />
            ) : (
              <Search
                placeholder="search orders"
                query={query}
                setQuery={setQuery}
              />
            )}
            <div className="flex gap-2 items-center">
              <Button
                text={
                  <div className="flex items-center justify-between w-[120px]">
                    <span>{orderPaymentType.title}</span>
                    <BiChevronDown size={18} />
                  </div>
                }
                id="basic-button"
                aria-controls={openDisc ? 'basic-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={openDisc ? 'true' : undefined}
                // @ts-ignore
                onClick={handleClickDiscMenu}
              />
              <Menu
                id="basic-menu"
                anchorEl={anchorEl}
                open={openDisc}
                onClose={handleCloseDiscMenu}
                MenuListProps={{
                  'aria-labelledby': 'basic-button',
                }}
                PaperProps={{
                  style: {
                    boxShadow: 'none',
                    marginTop: '5px',
                    border: '1px solid #4062FF',
                    height: 'fit-content',
                    backgroundColor: '#FFFFFF',
                    overflow: 'hidden',
                  },
                }}
              >
                {payment_status?.map((each: any, i: number) => (
                  <div
                    key={'payment' + i}
                    className={`font-Outfit px-2 py-2 hover:bg-almost-white font-normal text-[14px] leading-6  cursor-pointer `}
                    onClick={() => {
                      dispatch(updateOrderPaymentType(payment_status[i]))
                      dispatch(updateOrderFilters({page: 1, limit: filters.limit, totalPages: filters.totalPages})),
                      setAnchorEl(null);
                    }}
                  >
                    {each.title}
                  </div>
                ))}
              </Menu>
              <Datepicker
                name="date"
                className="w-[fit] appearance-none rounded-xl text-text-black placeholder:capitalize py-3 block placeholder:text-sm px-3 text-sm ring-1 ring-light-grey focus-within:ring-1 focus-within:ring-primary focus-within:outline-0 border-light-gray"
                locale="en-US"
                selected={orderStartDate}
                onChange={handleDateChange}
                startDate={orderStartDate}
                endDate={orderEndDate}
                selectsRange
                placeholderText="MM-DD-YYYY"
              />
            </div>
          </div>
            <Table
              pagination
              tableColumn={tableColumn}
              tableData={orderData}
              showCheckbox={true}
              loading={isLoading}
              onSelectRow={(selectedRows) => {
                setSelectedRows(selectedRows);
              }}
              serverPagination={{
                pageIndex: filters.page - 1,
                pageSize: filters.limit,
                totalPages: filters.totalPages,
                setPageIndex: (page: number) =>
                  dispatch(updateOrderFilters({page: page+1, limit: filters.limit, totalPages: filters.totalPages})),
                setPageSize: (limit: number) => {
                  setInitPage(true)
                  dispatch(updateOrderFilters({page: 1, limit, totalPages: filters.totalPages}))
                }
              }}
            />
        </div>
      </>
    </>
  );
};

export default Orders;
